import helpers from '../commonHelpers/helpers'
import { CONST } from '../const/const'
import { MESSAGES_CONST } from '../const/messages-const'
import { CustomError } from '../helpers/helpers'
import { IEventReviewPublish } from '../models/event-review-publish/event-review-publish.interface'
import { EventReviewPublishModel } from '../models/event-review-publish/event-review-publish.model'
import FirestoreService from './firestoreService'
import { getConvertedData } from '../models/interface.helper'
import UserService from './firebase-service/users/userService'
import { UserModel } from '../models/users/user.model'
import { IUserInterface } from '../models/users/user.interface'
import { EventDetailsModel } from '../models/event-details/event-details.model'

const CUSTOM_ERROR_PROPS = {
  fileName: 'eventService',
  message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
}

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

// Types

type IEventLocation = {
  city: string
  state: string
  country: string
  combinedLocation: string
}

class EventService {
  eventId: string
  private publishedEvent: IEventReviewPublish | null = null

  constructor(eventId: string) {
    if (!this.isValidEventId<string>(eventId))
      throw helpers.logger({
        message: CustomError.somethingWentWrong({
          devMessage: `eventId is ${eventId}`,
          ...CUSTOM_ERROR_PROPS,
        }),
      })

    this.eventId = eventId
  }

  private isValidEventId<T>(eventId: T): eventId is T {
    return !!eventId && typeof eventId === 'string'
  }

  private isPublishedEventNotEmpty<T>(publishedEvent: T): publishedEvent is T {
    if (!!publishedEvent) return true
    else throw new Error(`Publish event ${this.publishedEvent}`)
  }

  get getPublishedEvent(): Promise<IEventReviewPublish> {
    return new Promise(async (resolve, reject) => {
      try {
        if (this.publishedEvent) resolve(this.publishedEvent)

        const publishedEventSnapShot = await FirestoreService.getItem(
          COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
          this.eventId
        )

        if (!publishedEventSnapShot.exists())
          throw new Error(`Publish event with id: ${this.eventId} doesn't exist`)

        this.publishedEvent =
          EventReviewPublishModel.fromFirestoreDoc(publishedEventSnapShot).toObject()
        this.isPublishedEventNotEmpty(this.publishedEvent)
        return resolve(this.publishedEvent)
      } catch (error: any) {
        helpers.logger({
          message: CustomError.somethingWentWrong({
            devMessage: error?.message ?? error,
            ...CUSTOM_ERROR_PROPS,
          }),
        })
        reject(error)
      }
    })
  }

  get getPublishedEventDetails(): Promise<IEventReviewPublish['EventDetails'] | null> {
    let eventDetails: IEventReviewPublish['EventDetails'] | null = null

    return new Promise(async (resolve) => {
      const publishedEvent = await this.getPublishedEvent
      eventDetails = getConvertedData(new EventDetailsModel(publishedEvent.EventDetails).toObject())
      resolve(eventDetails)
    })
  }

  get getEventOwnerDetails(): Promise<IUserInterface | null> {
    let user: IUserInterface | null = null

    return new Promise<IUserInterface | null>(async (resolve) => {
      try {
        const eventDetails = await this.getPublishedEventDetails
        const eventOwnerId = eventDetails?.owner

        const { emptyVarName, emptyVarValue } = helpers.findEmptyVal({
          eventDetails,
          eventOwnerId,
        })

        if (!eventDetails || !eventDetails?.owner)
          throw helpers.logger({
            message: CustomError.somethingWentWrong({
              devMessage: `${emptyVarName} is ${emptyVarValue}`,
              moduleName: 'getEventOwnerDetails',
              ...CUSTOM_ERROR_PROPS,
            }),
          })

        const userSnapShot = await UserService.getUserInfoById(eventOwnerId)

        if (!userSnapShot.exists())
          throw helpers.logger({
            message: CustomError.somethingWentWrong({
              devMessage: `user with id: ${eventOwnerId} does not exist`,
              moduleName: 'getEventOwnerDetails',
              ...CUSTOM_ERROR_PROPS,
            }),
          })

        user = UserModel.fromFirestoreDoc(userSnapShot).toObject()
      } catch (error: any) {
        helpers.logger({
          message: error,
        })
      } finally {
        resolve(user)
      }
    })
  }

  get getEventLocation(): Promise<IEventLocation> {
    let seperator = ''

    let eventLocation: IEventLocation = {
      city: '',
      state: '',
      country: '',
      combinedLocation: '',
    }

    return new Promise(async (resolve) => {
      const eventDetails = await this.getPublishedEventDetails

      if (!eventDetails) return ''

      eventLocation = {
        ...eventLocation,
        city: eventDetails.competitionCity ?? '',
        state: eventDetails.competitionState ?? '',
        country: eventDetails.competitionCountry ?? '',
      }

      if (!!eventLocation?.city) {
        eventLocation.combinedLocation += eventLocation.city
        seperator = ', '
      } else seperator = ''

      if (!!eventLocation?.state) {
        eventLocation.combinedLocation += seperator + eventLocation.state
        seperator = ', '
      } else seperator = ''

      if (!!eventLocation?.country) {
        eventLocation.combinedLocation += seperator + eventLocation.country
        seperator = ', '
      } else seperator = ''

      resolve(eventLocation)
    })
  }

  getEventKey<K extends keyof IEventReviewPublish['EventDetails']>(
    keyToGet: K
  ): Promise<IEventReviewPublish['EventDetails'][K]> {
    let eventDetails: IEventReviewPublish['EventDetails'] | null = null

    return new Promise(async (resolve) => {
      eventDetails = await this.getPublishedEventKey('EventDetails')

      if (!eventDetails) return ''

      resolve(eventDetails[keyToGet])
    })
  }

  getPublishedEventKey<K extends keyof IEventReviewPublish>(
    keyToGet: K
  ): Promise<IEventReviewPublish[K]> {
    let publishedEvent = this.publishedEvent

    return new Promise(async (resolve) => {
      publishedEvent = await this.getPublishedEvent
      this.isPublishedEventNotEmpty<IEventReviewPublish>(publishedEvent)
      resolve(publishedEvent[keyToGet])
    })
  }
}

export default EventService
